import type { ReactNode, RefObject, UIEvent } from 'react'
import { AlertCircle, Bot, Globe, Loader2, User } from 'lucide-react'
import Markdown from 'react-markdown'
import { normalizeWebSourceLinks, SHORT_LAW_NAMES, stripInlineCitationMarkers } from '@/pages/chat/chat-utils'
import type { Citation, Message } from '@/pages/chat/chat-types'

type ChatMessagesPanelProps = {
  messages: Message[]
  error: string | null
  chatMessagesRef: RefObject<HTMLDivElement | null>
  onSelectCitation: (citation: Citation) => void
  onContainerClick: () => void
  topSlot?: ReactNode
  onScroll?: (event: UIEvent<HTMLDivElement>) => void
}

export function ChatMessagesPanel({
  messages,
  error,
  chatMessagesRef,
  onSelectCitation,
  onContainerClick,
  topSlot,
  onScroll,
}: ChatMessagesPanelProps) {
  return (
    <div
      ref={chatMessagesRef}
      className="chat-messages flex-1 min-h-0 overflow-y-auto"
      style={{ padding: '12px 16px' }}
      onClick={onContainerClick}
      onScroll={onScroll}
    >
      {topSlot && (
        <div style={{ marginBottom: '10px' }}>
          {topSlot}
        </div>
      )}
      {messages.length === 0 && (
        <div className="flex flex-col items-center justify-center text-center" style={{ paddingTop: '48px' }}>
          <div className="rounded-full bg-telegram-button/10 flex items-center justify-center" style={{ width: '56px', height: '56px', marginBottom: '16px' }}>
            <Bot size={28} className="text-telegram-button/60" />
          </div>
          <p className="font-medium text-sm" style={{ marginBottom: '6px' }}>Задайте юридический вопрос</p>
          <p className="text-telegram-hint" style={{ fontSize: '12px', maxWidth: '280px', lineHeight: '1.5' }}>
            Например: «Как расторгнуть договор аренды?» или «Какие права у работника при увольнении?»
          </p>
        </div>
      )}

      {messages.map((msg) => {
        const webSources = normalizeWebSourceLinks(msg.sources)
        return (
          <div key={msg.id} className={`chat-bubble-row flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} style={{ marginBottom: '10px' }}>
            <div className={`chat-bubble chat-bubble--${msg.role} max-w-[85%] ${msg.isStreaming && !msg.content ? 'min-w-[70%]' : ''} rounded-2xl ${
              msg.role === 'user' ? 'bg-telegram-button text-white' : 'glass-surface'
            }`} style={{ padding: '10px 14px' }}>
              <div className="flex items-center" style={{ gap: '6px', marginBottom: '4px' }}>
                {msg.role === 'assistant' ? <Bot size={12} className="text-telegram-button" /> : <User size={12} className="text-white/70" />}
                <span style={{ fontSize: '10px' }} className={msg.role === 'user' ? 'text-white/70' : 'text-telegram-hint'}>
                  {msg.role === 'user' ? 'Вы' : 'Ассистент'}
                </span>
                {msg.isStreaming && <Loader2 size={10} className="animate-spin text-telegram-button" />}
              </div>
              {msg.isStreaming && msg.thinkingStatus && !msg.content && (
                <div className="flex items-center gap-2 py-1" style={{ marginBottom: '4px' }}>
                  <div className="flex gap-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-telegram-button animate-pulse" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-telegram-button animate-pulse" style={{ animationDelay: '200ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-telegram-button animate-pulse" style={{ animationDelay: '400ms' }} />
                  </div>
                  <span className="text-[11px] text-telegram-hint leading-tight break-words">{msg.thinkingStatus?.replace(/^[\p{Emoji_Presentation}\p{Extended_Pictographic}\s]+/u, '')}</span>
                </div>
              )}
              <div className="text-sm leading-relaxed prose-sm prose-p:my-1 prose-headings:my-2 prose-li:my-0.5 prose-ul:my-1 prose-ol:my-1">
                {msg.content ? (
                  <Markdown
                    components={{
                      h1: ({ children }) => <h2 className="text-base font-bold mt-3 mb-1">{children}</h2>,
                      h2: ({ children }) => <h3 className="text-sm font-bold mt-2 mb-1">{children}</h3>,
                      h3: ({ children }) => <h4 className="text-sm font-semibold mt-2 mb-1">{children}</h4>,
                      p: ({ children }) => <p className="mb-1.5 last:mb-0">{children}</p>,
                      ul: ({ children }) => <ul className="list-disc pl-4 mb-1.5">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal pl-4 mb-1.5">{children}</ol>,
                      li: ({ children }) => <li className="mb-0.5">{children}</li>,
                      strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                      blockquote: ({ children }) => <blockquote className="border-l-2 border-telegram-button/40 pl-3 my-1.5 italic text-telegram-hint">{children}</blockquote>,
                      a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-telegram-link underline">{children}</a>,
                      code: ({ children }) => <code className="bg-telegram-secondary/80 rounded px-1 py-0.5 text-xs font-mono">{children}</code>,
                    }}
                  >
                    {stripInlineCitationMarkers(msg.content)}
                  </Markdown>
                ) : '...'}
              </div>
              {((msg.citations && msg.citations.length > 0) || webSources.length > 0) && (
                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid' }} className="border-telegram-hint/10">
                  <p className="text-telegram-hint" style={{ fontSize: '10px', marginBottom: '4px' }}>Источники:</p>
                  {msg.citations && msg.citations.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {msg.citations.map((citation, index) => (
                        <button
                          key={`${msg.id}-${index}`}
                          onClick={() => onSelectCitation(citation)}
                          className="text-[10px] font-medium px-2 py-0.5 rounded bg-telegram-button/10 text-telegram-button active:scale-95 transition-transform"
                        >
                          {SHORT_LAW_NAMES[citation.law_code || ''] || citation.law_name || citation.law_code}
                          {citation.article ? `, ст. ${citation.article}` : ''}
                        </button>
                      ))}
                    </div>
                  )}
                  {webSources.length > 0 && (
                    <div className="mt-1 flex flex-col gap-1">
                      {webSources.map((source) => (
                        <a
                          key={source.key}
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] bg-telegram-secondary/70 text-telegram-link"
                        >
                          <Globe size={10} />
                          <span className="truncate">{source.title}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )
      })}

      {error && (
        <div className="flex items-center rounded-xl bg-red-500/10 border border-red-500/20" style={{ padding: '10px 14px', gap: '8px', marginBottom: '10px' }}>
          <AlertCircle size={16} className="text-red-500 shrink-0" />
          <p className="text-xs text-red-500">{error}</p>
        </div>
      )}
    </div>
  )
}
